import { useMemo } from 'react';

import { useUserInfoState } from '@kc-console/context';
import { STATUS_CODE } from '@kc-console/core';
import {
  InServiceMenuUnsupported,
  InServiceNoOrgPermission,
  InServiceNoProjectPermission,
  InServiceNotFound,
  InServiceTemporaryError,
} from '@kc-console/shared-ui';

import DomainRoleGuideLinkButton from '../InServiceError/components/DomainRoleGuideLinkButton';
import MyRoleGuideLinkButton from '../InServiceError/components/MyRoleGuideLinkButton';
import ProjectRoleGuideLinkButton from '../InServiceError/components/ProjectRoleGuideLinkButton';
import { InServiceErrorLayout } from './InServiceErrorLayout';

import type { StatusCode } from '@kc-console/core';

type InServiceErrorMapperProps = {
  status: StatusCode;
};

/**
 * 서비스 내 에러 매퍼 컴포넌트
 *
 * @description
 * 상태 코드에 따라 적절한 서비스 내 에러 컴포넌트를 렌더링합니다.
 * InServiceErrorLayout으로 감싸서 표시됩니다.
 */
const InServiceErrorMapper = ({ status }: InServiceErrorMapperProps) => {
  const { refetch: refetchUserInfo, isRefetching } = useUserInfoState();
  const renderError = () => {
    if (status === STATUS_CODE.INTERNAL_SERVER_ERROR_IN_SERVICE) {
      return <InServiceTemporaryError />;
    } else if (status === STATUS_CODE.NOT_FOUND_ROUTE) {
      return <InServiceNotFound />;
    } else if (status === STATUS_CODE.FORBIDDEN_REGION_SUB) {
      return <InServiceMenuUnsupported />;
    } else if (status === STATUS_CODE.FORBIDDEN_DOMAIN) {
      /**
       * 도메인 권한 없음은 InService 에서 도메인 권한 없음이 적절하지 않음.
       * 역할에 따른 처리는 서비스에서 PageRoleBoundary로 처리하도록 가이드.
       * 기획에 맞춰 개발은 해두되 변경이 필요함
       */
      return <InServiceNoOrgPermission onRefresh={refetchUserInfo} isRefreshing={isRefetching} />;
    } else if (status === STATUS_CODE.FORBIDDEN_PROJECT) {
      /**
       * 프로젝트 권한 없음은 InService 에서 도메인 권한 없음이 적절하지 않음.
       * 역할에 따른 처리는 서비스에서 PageRoleBoundary로 처리하도록 가이드.
       * 기획에 맞춰 개발은 해두되 변경이 필요함
       */
      return (
        <InServiceNoProjectPermission onRefresh={refetchUserInfo} isRefreshing={isRefetching} />
      );
    } else {
      return <InServiceTemporaryError />;
    }
  };

  const usefulInfoActions = useMemo(() => {
    if (status === STATUS_CODE.FORBIDDEN_DOMAIN) {
      return (
        <>
          <MyRoleGuideLinkButton />
          <DomainRoleGuideLinkButton />
        </>
      );
    } else if (status === STATUS_CODE.FORBIDDEN_PROJECT) {
      return (
        <>
          <MyRoleGuideLinkButton />
          <ProjectRoleGuideLinkButton />
        </>
      );
    }
  }, [status]);

  return (
    <InServiceErrorLayout
      usefulInfoProps={usefulInfoActions ? { actions: usefulInfoActions } : undefined}
    >
      {renderError()}
    </InServiceErrorLayout>
  );
};

export default InServiceErrorMapper;
